// TemplateBodyBuilder.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Button, Card, CardContent, Divider, FormControl, Grid, IconButton,
    InputLabel, MenuItem, Select, Stack, TextField, Typography, Tooltip
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

// =============== Конфиг действий (+ param-описания) ===============
const ACTION_DEFS = {
    send_message_telegram: {
        label: "Отправить сообщение в Telegram",
        fields: [
            { key: "message",     label: "Сообщение",   mode: "text", required: true, isUi: true, param: { name: "message", source: "EXTERNAL", required: true } },
            { key: "telegram_id", label: "Telegram ID", mode: "var", required: true, isUi: false, param: { name: "telegram_id", source: "INTERNAL", resolverPath: "context_telegram_id", required: true } }
        ],
    },
    send_message_vk: {
        label: "Отправить сообщение в VK",
        fields: [
            { key: "message",     label: "Сообщение",   mode: "text", required: true, isUi: true, param: { name: "message", source: "EXTERNAL", required: true } },
            { key: "vk_id", label: "VK ID", mode: "var", required: true, isUi: false, param: { name: "vk_id", source: "INTERNAL", resolverPath: "context_vk_id", required: true } }
        ],
    },
};

// =============== Утилиты ===============
const rid = () => Math.random().toString(36).slice(2, 10);
const isBlank = (s) => !s || String(s).trim().length === 0;

// Сборка JSON-массива действий
function buildTemplateJson(actions) {
    return actions.map(a => {
        const def = ACTION_DEFS[a.typeKey];
        const body = {};
        (def?.fields || []).forEach(f => {
            const cell = a.body?.[f.key];
            if (!cell) return;
            if (f.mode === "var") {
                if (!isBlank(cell.value)) body[f.key] = { var: String(cell.value) };
            } else {
                if (!isBlank(cell.value)) body[f.key] = String(cell.value);
            }
        });
        return { type: a.typeKey, body };
    });
}

function validateActions(actions) {
    const errors = {};
    actions.forEach((a, idx) => {
        const def = ACTION_DEFS[a.typeKey];
        if (!def) {
            errors[idx] = { _base: "Выберите тип действия" };
            return;
        }
        const fieldErrors = {};
        (def.fields || []).forEach(f => {
            if (f.required) {
                const cell = a.body?.[f.key];
                if (!cell || isBlank(cell.value)) fieldErrors[f.key] = "Обязательное поле";
            }
        });
        if (Object.keys(fieldErrors).length) errors[idx] = fieldErrors;
    });
    return errors;
}

// ======== Гарантируем наличие параметра: ищем по resolverPath -> по name, иначе создаём ========
function ensureParam(fieldParam, currentParams, updateParams) {
    if (!fieldParam) return null;
    const { name, resolverPath } = fieldParam;

    let found = null;
    if (resolverPath) {
        found = currentParams.find(p => p?.resolverPath === resolverPath);
    }
    if (!found && name) {
        found = currentParams.find(p => p?.name === name);
    }
    if (found) return found;

    // создаём «минимальный» параметр (под ваши структуры можно расширить)
    const newParam = {
        id: rid(),
        ...fieldParam
    };

    const next = [...currentParams, newParam];
    updateParams(next); // локально + наружу
    return newParam;
}

// =============== Компонент ===============
/**
 * @param {Array} params           - список параметров из вне (минимум: {id?, name, resolverPath?})
 * @param {String} value           - исходный JSON templateBody (строка)
 * @param {Function} onChange      - (jsonString) => void
 * @param {Function} onParamsChange- (nextParamsArray) => void  // <--- НОВОЕ
 */
export default function TemplateBodyBuilder({ params = [], value, onChange, onParamsChange }) {
    // Держим локальную копию params, чтобы можно было добавлять и синхронизировать наружу
    const [paramList, setParamList] = useState(params);
    useEffect(() => {
        setParamList(params);
    }, [params]);

    const updateParamsBoth = (next) => {
        setParamList(next);
        onParamsChange && onParamsChange(next);
    };

    // Начальное действие
    const [actions, setActions] = useState([]);

    const [errors, setErrors] = useState({});

    // Восстановление из value (если пришёл JSON)
    useEffect(() => {
        if (!value || isBlank(value)) return;
        try {
            const arr = JSON.parse(value);
            if (!Array.isArray(arr)) return;
            const restored = arr.map(act => {
                const typeKey = act.type;
                const def = ACTION_DEFS[typeKey];
                const body = {};
                (def?.fields || []).forEach(f => {
                    const raw = act.body?.[f.key];
                    if (f.mode === "var") {
                        const v = raw && typeof raw === "object" && raw.var ? String(raw.var) : "";
                        body[f.key] = { mode: "var", value: v };
                    } else {
                        body[f.key] = { mode: "value", value: raw ? String(raw) : "" };
                    }
                });
                return { id: rid(), typeKey, body };
            });
            if (restored.length) setActions(restored);
        } catch (e){
            console.log(e)
            /* игнорируем невалидный JSON */
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ======== Автодобавление/подстановка params для действий с описанием param ========
    useEffect(() => {
        setActions(prev => prev.map(a => {
            const def = ACTION_DEFS[a.typeKey];
            if (!def) return a;

            const next = { ...a, body: { ...(a.body || {}) } };

            (def.fields || []).forEach(f => {
                if (!f.param || f.mode !== 'var') return;

                // гарантируем наличие param (по resolverPath -> name, иначе создадим)
                const ensured = ensureParam(f.param, paramList, updateParamsBoth);
                const ensuredName = ensured?.name || f.param?.name || "";

                // если поле пустое — подставим найденное имя
                const cell = next.body[f.key] || { mode: f.mode, value: "" };
                if (isBlank(cell.value)) {
                    next.body[f.key] = { mode: f.mode, value: ensuredName };
                } else {
                    next.body[f.key] = cell;
                }
            });

            return next;
        }));
        // триггеры: список параметров менялся или список действий
    }, [paramList, actions.length]);

    // Превью JSON + валидация
    const jsonPreview = useMemo(() => {
        const errs = validateActions(actions);
        setErrors(errs);
        const json = buildTemplateJson(actions);
        const str = JSON.stringify(json, null, 2);
        onChange && onChange(str);
        return str;
    }, [actions, onChange]);

    // Опции выбора параметров (из актуального paramList)
    const paramOptions = useMemo(
        () => (paramList || [])
            .map(p => ({ id: p.id ?? p.name ?? rid(), name: p.name || "" }))
            .filter(p => !isBlank(p.name)),
        [paramList]
    );

    function addAction() {
        const firstTypeKey = Object.keys(ACTION_DEFS)[0];
        const def = ACTION_DEFS[firstTypeKey];
        const initBody = {};
        (def.fields || []).forEach(f => {
            initBody[f.key] = { mode: f.mode, value: "" };
        });
        setActions(prev => [...prev, { id: rid(), typeKey: firstTypeKey, body: initBody }]);
    }

    function removeAction(idx) {
        setActions(prev => prev.filter((_, i) => i !== idx));
    }

    function changeType(idx, nextTypeKey) {
        const def = ACTION_DEFS[nextTypeKey];
        const initBody = {};
        (def.fields || []).forEach(f => {
            initBody[f.key] = { mode: f.mode, value: "" };
        });
        setActions(prev => prev.map((a, i) => i === idx ? { ...a, typeKey: nextTypeKey, body: initBody } : a));
    }

    function changeField(idx, fieldKey, nextValue) {
        setActions(prev => prev.map((a, i) => {
            if (i !== idx) return a;
            const body = { ...(a.body || {}) };
            const cell = body[fieldKey] || { mode: "var", value: "" };
            body[fieldKey] = { ...cell, value: nextValue };
            return { ...a, body };
        }));
    }

    return (
        <Card>
            <CardContent>
                <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle1">Что должно быть выполнено?</Typography>
                        <Tooltip title="Добавить действие">
                            <Button startIcon={<AddIcon />} onClick={addAction} size="small" variant="outlined">
                                Добавить действие
                            </Button>
                        </Tooltip>
                    </Stack>

                    {actions.length === 0 && (
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Нет действий. Нажмите «Добавить действие».
                        </Typography>
                    )}

                    {actions.map((a, idx) => {
                        const def = ACTION_DEFS[a.typeKey];
                        const rowErrs = errors[idx] || {};
                        return (
                            <Box key={a.id} sx={{ p: 1.5, borderRadius: 2, border: "1px dashed rgba(255,255,255,0.25)" }}>
                                <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <FormControl size="small" sx={{ minWidth: 260 }}>
                                        <InputLabel>Тип действия</InputLabel>
                                        <Select
                                            label="Тип действия"
                                            value={a.typeKey}
                                            onChange={(e) => changeType(idx, e.target.value)}
                                        >
                                            {Object.entries(ACTION_DEFS).map(([key, meta]) => (
                                                <MenuItem key={key} value={key}>{meta.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Box flex={1} />

                                    <Tooltip title="Удалить действие">
                                        <IconButton onClick={() => removeAction(idx)} color="error">
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>

                                {/* Поля body зависят от выбранного type */}
                                <Grid container spacing={1}>
                                    {(def?.fields || []).map(f => {
                                        const cell = a.body?.[f.key] || { mode: f.mode, value: "" };
                                        const err = rowErrs[f.key];

                                        if (!f.isUi) {
                                            return <></>
                                        }

                                        if (f.mode === "var") {
                                            return (
                                                <Grid key={f.key} item xs={12} md={6}>
                                                    <FormControl size="small" fullWidth error={!!err}>
                                                        <InputLabel>{`${f.label}${f.required ? " *" : ""}`}</InputLabel>
                                                        <Select
                                                            label={`${f.label}${f.required ? " *" : ""}`}
                                                            value={cell.value || ""}
                                                            onChange={(e) => changeField(idx, f.key, e.target.value)}
                                                        >
                                                            {paramOptions.length === 0 && (
                                                                <MenuItem value="">(нет доступных params)</MenuItem>
                                                            )}
                                                            {paramOptions.map(po => (
                                                                <MenuItem key={po.id} value={po.name}>{po.name}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                    {err && <Typography color="error" variant="caption">{err}</Typography>}
                                                </Grid>
                                            );
                                        }

                                        return (
                                            <Grid key={f.key} item xs={12} md={6}>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    label={`${f.label}${f.required ? " *" : ""}`}
                                                    value={cell.value || ""}
                                                    onChange={(e) => changeField(idx, f.key, e.target.value)}
                                                    error={!!err}
                                                    helperText={err ? err : "литеральное значение (строка)"}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        );
                    })}

                    {/*<Divider />*/}

                    {/*<Typography variant="subtitle2">JSON-предпросмотр</Typography>*/}
                    {/*<TextField*/}
                    {/*    value={jsonPreview}*/}
                    {/*    onChange={() => {}}*/}
                    {/*    fullWidth*/}
                    {/*    multiline*/}
                    {/*    minRows={8}*/}
                    {/*    InputProps={{ readOnly: true }}*/}
                    {/*/>*/}
                </Stack>
            </CardContent>
        </Card>
    );
}
